import { Space } from "@gocardless/flux-react";
import { Plural, Trans } from "@lingui/macro";

import { SignInApiErrors } from "../../SignIn/useSignIn";

type SignInApiErrorComponentMap = {
  [key in SignInApiErrors]?: React.ReactElement;
};

export const TopMessage = ({
  errorType,
}: {
  errorType: SignInApiErrors;
}): React.ReactElement => {
  const topMessageMap: SignInApiErrorComponentMap = {
    [SignInApiErrors.RequirePasswordResetForPasswordPolicy]: (
      <Trans id="forced-password-reset-message.top.weak">
        To help keep your account secure, we require you to update your
        password.
      </Trans>
    ),
    [SignInApiErrors.RequirePasswordResetForDormantAccount]: (
      <Trans id="forced-password-reset-message.top.dormant">
        To help keep your account secure, we require you to update your
        password.
      </Trans>
    ),
    [SignInApiErrors.RequirePasswordResetForAccountUnlock]: (
      <Trans id="forced-password-reset-message.top.locked">
        We&apos;ve detected some suspicious activity. To help keep your account
        secure, we require you to update your password.
      </Trans>
    ),
    [SignInApiErrors.RequirePasswordResetForResetDelayExpiry]: (
      <Trans id="forced-password-reset-message.top.safety">
        We&apos;ve had to secure your account because your password doesn&apos;t
        meet our safety requirements.
      </Trans>
    ),
  };

  return topMessageMap[errorType] ?? <></>;
};

export const BottomMessage = ({
  errorType,
  daysUntilRequiredPasswordReset,
}: {
  errorType: SignInApiErrors;
  daysUntilRequiredPasswordReset?: string;
}): React.ReactElement => {
  const numberOfDays = daysUntilRequiredPasswordReset && (
    <Plural
      id="in-pluralized-day"
      value={daysUntilRequiredPasswordReset}
      one="in # day"
      other="in # days"
    />
  );

  const bottomMessageMap: SignInApiErrorComponentMap = {
    [SignInApiErrors.RequirePasswordResetForPasswordPolicy]: (
      <>
        <Trans id="forced-password-reset-message.bottom.weak">
          We have sent an email with further instructions to the address
          registered with your GoCardless account. If you haven&apos;t received
          it in a few minutes, please check your spam folder or click below to
          resend the email.
        </Trans>
        <Space v={1} h={1} />
        <Trans id="forced-password-reset-message.bottom.continue-to-dashboard">
          You can continue to the dashboard for now, but we will be enforcing a
          password reset{numberOfDays && " "}
          {numberOfDays}.
        </Trans>
      </>
    ),
    [SignInApiErrors.RequirePasswordResetForDormantAccount]: (
      <Trans id="forced-password-reset-message.bottom.dormant">
        We have sent an email with further instructions to the address
        registered with your GoCardless account. If you haven&apos;t received it
        in a few minutes, please check your spam folder or click below to resend
        the email.
      </Trans>
    ),

    [SignInApiErrors.RequirePasswordResetForAccountUnlock]: (
      <Trans id="forced-password-reset-message.bottom.locked">
        We have sent an email with further instructions to the address
        registered with your GoCardless account. If you haven&apos;t received it
        in a few minutes, please check your spam folder or click below to resend
        the email.
      </Trans>
    ),
    [SignInApiErrors.RequirePasswordResetForResetDelayExpiry]: (
      <Trans id="forced-password-reset-message.bottom.safety">
        We previously sent an email with further instructions to the address
        registered with your GoCardless account. If you haven&apos;t received
        it, please check your spam folder or click below to resend the email.
      </Trans>
    ),
  };

  return bottomMessageMap[errorType] ?? <></>;
};
